// eslint-disable @typescript-eslint/no-non-null-assertion
import { Divider, Stack } from 'component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GraphFact } from '@/components/Charts/types';
import {
  NCCard,
  NCCard2SectionLayout,
  NCCardAnalysisTypeToggle,
  NCCardComplianceFooter,
  NCCardHeader,
  useNCCardContext,
} from '@/components/NCCard/NCCard';
import { AboveGroundCarbonExplainer } from '@/components/NCExplainers/AboveGroundCarbonExplainer';
import { COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON } from '@/config/constants';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { useBenchmarkSelection } from '@/hooks/useBenchmarkSelection';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { AboveGroundCarbonCardGraphTile } from './GraphTile';
import { AboveCarbonCardStatsTile } from './StatsTile';

export const AboveGroundCarbon = () => {
  const { t } = useTranslation();
  const factNames = useAboveGroundCarbonFacts();

  const [showCard, setShowCard] = useState(true);

  if (!showCard || isNullish(factNames)) {
    return null;
  }

  return (
    <PlotCardWrapper name={t('global.analysis.carbonStorageAg')}>
      <NCCard explainer={<AboveGroundCarbonExplainer />} data-testid='above-ground-carbon-nca'>
        <AboveGroundCarbonPlotCard setShowCard={setShowCard} />
      </NCCard>
    </PlotCardWrapper>
  );
};

const AboveGroundCarbonPlotCard = ({ setShowCard }: { setShowCard: (show: boolean) => void }) => {
  const { t } = useTranslation();
  const isSmallScreen = useScreenSize() === 'small';
  const { analysisType } = useNCCardContext();

  const factNames = useAboveGroundCarbonFacts();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const factSet = analysisType === 'total' ? factNames!.total : factNames!.perHa;

  const facts = usePlotFacts(Object.values(factSet ?? {}));

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const graphData = getFactFromFactArray<GraphFact[]>(facts, factSet!.graph!)?.value;
  // const benchmarkFact = {
  //   high: getFactFromFactArray<GraphFact[]>(facts, factSet.benchmark, {
  //     [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  //   }),
  //   moderate: getFactFromFactArray<GraphFact[]>(facts, factSet.benchmark, {
  //     [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  //   }),
  //   low: getFactFromFactArray<GraphFact[]>(facts, factSet.benchmark, {
  //     [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  //   }),
  // };
  const benchmarkFact = {
    high: undefined,
    moderate: undefined,
    low: undefined,
  };

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.historic_above_ground_carbon;
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useBenchmarkSelection(indicator);

  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);

  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmarkId);

  const plot = usePlot().data;
  const plotArea = plot.area;

  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal, plotArea);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const latestAnalysisFact = getFactFromFactArray<number>(facts, factSet!.latestAnalysis!);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const oneYearUpliftFact = getFactFromFactArray<number>(facts, factSet!.oneYearUplift!);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const oneYearPctChangeFact = getFactFromFactArray<number>(facts, factSet!.oneYearPctChange!);
  const hasChartData = !isNullish(graphData);
  const hasStatsData =
    !isNullish(latestAnalysisFact?.value) ||
    !isNullish(oneYearUpliftFact?.value) ||
    !isNullish(oneYearPctChangeFact?.value);

  useEffect(() => {
    if (!hasChartData && !hasStatsData) {
      setShowCard(false);
    }
  }, [hasChartData, hasStatsData, setShowCard]);

  return (
    <>
      <NCCardHeader title={t('global.analysis.carbonStorageAg')}>
        {!isSmallScreen && <NCCardAnalysisTypeToggle data-testid='carbon-toggle' />}
      </NCCardHeader>
      <NCCard2SectionLayout>
        <Stack className='items-center pt-4 sm:items-stretch sm:pt-0'>
          {isSmallScreen && <NCCardAnalysisTypeToggle data-testid='carbon-toggle' />}
          <AboveGroundCarbonCardGraphTile
            graphData={graphData}
            benchmarkFact={benchmarkFact}
            activeCustomBenchmark={displayCustomBenchmark}
            selectedBenchmarkId={selectedBenchmarkId}
            onBenchmarkSelect={setSelectedBenchmarkId}
          />
        </Stack>
        <AboveCarbonCardStatsTile
          latestAnalysisFact={latestAnalysisFact}
          oneYearUpliftFact={oneYearUpliftFact}
          oneYearPctChangeFact={oneYearPctChangeFact}
          benchmarkFact={benchmarkFact}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON} />
    </>
  );
};
